import styled, { css, keyframes } from 'styled-components';
import { FADE, RISE_MS, t } from 'theme';

/**
 * The town is the only milestone on a westward walk, so the name is the only large type in the
 * game. It is set in the face the map uses for place labels ('Palatino Linotype', index.css),
 * because a town belongs to the world rather than to the interface, and it sits on a hairline
 * that runs off to the right edge — the horizon, with the one thing standing on it.
 *
 * There is no panel here on purpose. Everything reads off the page, so the town inherits both
 * palettes and the dusk fade without a surface of its own.
 */

const MAP_FACE = `'Palatino Linotype', 'Book Antiqua', Palatino, serif`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: none; }
`;

// One moment, in two beats: the sign, then what the street is doing. Anything more would be
// decoration on a screen that is meant to be still.
//
// Deliberately on RISE_MS rather than the day/night fade. Arriving somewhere and the sun
// going down are different lengths of time — the town's name should land, not drift in.
const arrive = delay => css`
  animation: ${rise} ${RISE_MS}ms ease both;
  animation-delay: ${delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const City = styled.div`
  width: 100%;
  max-width: 44rem;
  text-align: left;
`;

const Sign = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  ${arrive(0)}
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${MAP_FACE};
  font-weight: 400;
  font-size: clamp(1.75rem, 6vw, 2.5rem);
  line-height: 1.1;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${t('fg')};
  transition: color ${FADE};

  /* Letterspacing leaves a gap after the last glyph. Pull the rule back onto it. */
  margin-right: -0.28em;

  /* Flex items refuse to shrink past their content by default, which sends a long name like
     WRAITH'S CROSSING off the right edge. Let it wrap and ease the tracking when it is tight. */
  min-width: 0;
  overflow-wrap: break-word;

  @media (max-width: 640px) {
    letter-spacing: 0.16em;
    margin-right: -0.16em;
  }
`;

const Horizon = styled.span`
  flex: 1;
  min-width: 2rem;
  height: 1px;
  background: ${t('rule')};
  transition: background ${FADE};
`;

// Territories are set small-caps on the map (index.css, text.region). Same treatment here, so
// the line under the name reads as the same kind of label in both places.
const Territory = styled.div`
  margin-top: 0.65rem;
  font-family: ${MAP_FACE};
  font-variant: small-caps;
  font-size: 0.9rem;
  letter-spacing: 0.14em;
  color: ${t('muted')};
  transition: color ${FADE};
`;

const Observations = styled.div`
  margin: 2.5rem 0 3.25rem;
  max-width: 46ch;
  font-family: serif;
  font-size: 1rem;
  line-height: 1.7;
  color: ${t('fg')};
  transition: color ${FADE};
  ${arrive(260)}
`;

const Observation = styled.p`
  margin: 0 0 0.65rem;
`;

const Activities = styled.span`
  display: inline-flex;
  flex-direction: column;
`;

const Actions = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
`;

// What you can do here, and what you can buy here — two groups, kept apart on purpose. Capped
// so the split reads as a gutter rather than the canyon it becomes at full column width.
const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  max-width: 34rem;
`;

/**
 * The way out.
 *
 * It is deliberately not a third column beside beg and trade post: leaving is not something you
 * do here, and standing it next to the things that are makes it read as one of them. It is the
 * end of the page instead, so the reading order and the decision order are the same one — every
 * reason to stay is passed before the way out is reached.
 *
 * The rule is the same `Horizon` the town's name stands on, running to the same right edge, so
 * the screen opens and closes on the same hairline: the top one is where you arrived, this one
 * is where you go. West is right and the control hangs off the western end of it, which is why
 * there is no arrow on the label — the line already points.
 */
const Departure = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 3.25rem;

  /* Buttons carry a bottom margin for stacking in the columns above. This one is alone on its
     line, and that margin would sit the rule off its centre. */
  > button {
    margin-bottom: 0;
  }
`;

export {
  Actions,
  Activities,
  ButtonContainer,
  City,
  Departure,
  Horizon,
  Name,
  Observation,
  Observations,
  Sign,
  Territory
};
